import './ChannelPerformance.scss';
import { useEffect, useMemo, useState } from 'react';
import { MdBarChart, MdShowChart, MdTableChart } from 'react-icons/md';
import { Background, Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { MenuItem } from '@Components/Dropdown/dropdown-constants';
import { IconSegmentedControlButton } from '@Components/SegmentedControlButton';
import { WidgetContainer } from '@Components/WidgetContainer';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { ChannelPerformanceBars } from './ChannelPerformanceBars';
import { ChannelPerformanceLine } from './ChannelPerformanceLine';
import { ChannelPerformanceTable } from './ChannelPerformanceTable';
import { DashboardData, HierarchyLevel, HierarchyLevelConst } from '../dtc-commerce-mmm-types';
import { formatFieldName } from '../hooks/useChannelAggregation';
import { shapeBarTableExport, shapeLineExport } from '../utils/dtc-commerce-mmm-export';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';

const ChannelPerformanceViewMode = {
  BARS: 'bars',
  TABLE: 'table',
  LINE: 'line'
} as const;
type ChannelPerformanceViewMode =
  (typeof ChannelPerformanceViewMode)[keyof typeof ChannelPerformanceViewMode];

const VIEW_OPTIONS = [
  { key: ChannelPerformanceViewMode.BARS, icon: <MdBarChart />, label: 'Bar view' },
  { key: ChannelPerformanceViewMode.TABLE, icon: <MdTableChart />, label: 'Table view' },
  { key: ChannelPerformanceViewMode.LINE, icon: <MdShowChart />, label: 'Over time' }
] as const;

const HIERARCHY_TITLE_LABELS: Record<HierarchyLevel, string> = {
  [HierarchyLevelConst.CHANNEL]: 'Channel',
  [HierarchyLevelConst.PLATFORM]: 'Platform',
  [HierarchyLevelConst.TACTIC]: 'Tactic',
  [HierarchyLevelConst.BRAND_NONBRAND]: 'Tactic'
};

const channelFilterDisplay = (count: number): string => {
  if (count === 0) return 'All Channels';
  if (count === 1) return '1 Channel Selected';
  return `${count} Channels Selected`;
};

interface ChannelPerformanceProps {
  data: DashboardData;
  hierarchyLevel: HierarchyLevel;
  onChannelClick: (groupKey: string, focusedFeatureName?: string) => void;
  startOpen?: boolean;
}

export const ChannelPerformance = ({
  data,
  hierarchyLevel,
  onChannelClick,
  startOpen = true
}: ChannelPerformanceProps) => {
  const [viewMode, setViewMode] = useState<ChannelPerformanceViewMode>(
    ChannelPerformanceViewMode.BARS
  );
  // Applied channel filter. Persists across view-mode changes; only affects the
  // three views in this container.
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);

  const isRevenue = data.modelInfo.kpiType === 'revenue';
  const hierarchyLabel = HIERARCHY_TITLE_LABELS[hierarchyLevel];

  const filteredData: DashboardData = useMemo(() => {
    if (selectedChannels.length === 0) return data;
    const set = new Set(selectedChannels);
    return {
      ...data,
      actualized: {
        ...data.actualized,
        cptbT12M: data.actualized.cptbT12M.filter(r => set.has(r.channel))
      },
      raw: {
        ...data.raw,
        cptbT12M: data.raw.cptbT12M.filter(r => set.has(r.channel))
      }
    };
  }, [data, selectedChannels]);

  const { buildExportFilename } = useDtcCommerceMmmData();

  // Download is view-aware. Exports always use the unfiltered `data` (not
  // `filteredData`) so the file reflects the full feature-level dataset,
  // independent of the in-container channel filter.
  const handleExcelDownload = () => {
    const { client } = data.modelInfo;
    if (viewMode === ChannelPerformanceViewMode.LINE) {
      exportMultiSheetToExcel(
        shapeLineExport(data),
        buildExportFilename(client, 'channel-performance-line')
      );
      return;
    }
    const visual =
      viewMode === ChannelPerformanceViewMode.TABLE
        ? 'channel-performance-table'
        : 'channel-performance-bar';
    exportMultiSheetToExcel(shapeBarTableExport(data), buildExportFilename(client, visual));
  };

  const pngVisualFor = (mode: ChannelPerformanceViewMode): string => {
    if (mode === ChannelPerformanceViewMode.LINE) return 'channel-performance-line';
    if (mode === ChannelPerformanceViewMode.TABLE) return 'channel-performance-table';
    return 'channel-performance-bar';
  };

  const handlePngDownload = () => {
    const { client } = data.modelInfo;
    downloadPNG('.channel-performance', buildExportFilename(client, pngVisualFor(viewMode)));
  };

  return (
    <WidgetContainer
      background={Background.DARK}
      collapsible
      startOpen={startOpen}
      header={`${hierarchyLabel} Performance`}
      subHeader={`Actualized spend, contribution, and return on ad spend by ${hierarchyLabel.toLowerCase()}`}
      rightActions={
        <div className='channel-performance-right-actions'>
          <ChannelFilter
            data={data}
            selectedChannels={selectedChannels}
            onChange={setSelectedChannels}
          />
          <IconSegmentedControlButton
            size={Size.LG}
            options={VIEW_OPTIONS.map(o => ({ key: o.key, icon: o.icon, label: o.label }))}
            selectedOption={viewMode}
            onChange={key => setViewMode(key as ChannelPerformanceViewMode)}
          />
          <DownloadDropdown onClickOptions={[handleExcelDownload, handlePngDownload]} />
        </div>
      }
    >
      <div className='channel-performance'>
        {viewMode === ChannelPerformanceViewMode.BARS && (
          <ChannelPerformanceBars
            data={filteredData}
            isRevenue={isRevenue}
            hierarchyLevel={hierarchyLevel}
            onChannelClick={onChannelClick}
          />
        )}
        {viewMode === ChannelPerformanceViewMode.TABLE && (
          <ChannelPerformanceTable
            data={filteredData}
            isRevenue={isRevenue}
            hierarchyLevel={hierarchyLevel}
            onChannelClick={onChannelClick}
          />
        )}
        {viewMode === ChannelPerformanceViewMode.LINE && (
          <ChannelPerformanceLine data={filteredData} hierarchyLevel={hierarchyLevel} />
        )}
      </div>
    </WidgetContainer>
  );
};

interface ChannelFilterProps {
  data: DashboardData;
  selectedChannels: string[];
  onChange: (channels: string[]) => void;
}

const ChannelFilter = ({ data, selectedChannels, onChange }: ChannelFilterProps) => {
  // Always channel-level options, alphabetically sorted; order doesn't shift
  // as the user toggles selections.
  const channelOptions = useMemo(
    () =>
      [...data.channelHierarchy.uniqueChannels]
        .map(channel => ({ value: channel, label: formatFieldName(channel) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data.channelHierarchy.uniqueChannels]
  );

  // Local pending selection used while the menu is open; the bootstrap
  // multi-select Dropdown ticks items based on the `selected` flag on each
  // MenuItem, so we mirror clicks into this set and rebuild menuItems from it.
  // Resets to the committed value whenever the applied selection changes.
  const [pending, setPending] = useState<Set<string>>(() => new Set(selectedChannels));
  useEffect(() => {
    setPending(new Set(selectedChannels));
  }, [selectedChannels]);

  const channelMenuItems: MenuItem<string>[] = useMemo(
    () =>
      channelOptions.map(opt => ({
        value: opt.value,
        label: opt.label,
        selected: pending.has(opt.value)
      })),
    [channelOptions, pending]
  );

  const channelDisplay = channelFilterDisplay(selectedChannels.length);

  return (
    <Dropdown<string>
      overlayPlacement={Placement.RIGHT}
      className='channel-performance-channel-dropdown'
      dropdownMenuClassName='channel-performance-channel-menu'
      multiSelect
      clearable={selectedChannels.length > 0}
      onClear={() => {
        setPending(new Set());
        onChange([]);
      }}
      options={channelOptions}
      menuItems={channelMenuItems}
      value={channelDisplay}
      onChange={() => {}}
      itemOnClick={item => {
        setPending(prev => {
          const next = new Set(prev);
          if (next.has(item.value)) {
            next.delete(item.value);
          } else {
            next.add(item.value);
          }
          return next;
        });
      }}
      applyOnClick={() => {
        // "All N selected" is semantically the same as no filter — collapse
        // back to an empty filter so the toggle reads "All Channels" again.
        const isAll = pending.size === channelOptions.length;
        onChange(isAll ? [] : [...pending]);
      }}
      cancelOnClick={() => {
        setPending(new Set(selectedChannels));
      }}
    />
  );
};
